import React, { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Info, History } from 'lucide-react'

interface VersionInfo {
  version: string
  agentType: string
  displayName: string
  releaseDate: string
}

interface VersionIndicatorProps {
  versionInfo: VersionInfo
  onViewChangelog?: () => void
  variant?: 'default' | 'secondary' | 'outline'
  size?: 'sm' | 'default' | 'lg'
  showChangelog?: boolean
}

export const VersionIndicator: React.FC<VersionIndicatorProps> = ({
  versionInfo,
  onViewChangelog,
  variant = 'secondary',
  size = 'sm',
  showChangelog = true
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const tooltipContent = (
    <div className="space-y-1 max-w-xs">
      <div className="font-medium">{versionInfo.displayName}</div>
      <div className="text-xs text-muted-foreground">
        Version: {versionInfo.version}
      </div>
      <div className="text-xs text-muted-foreground">
        Released: {formatDate(versionInfo.releaseDate)}
      </div>
      {showChangelog && onViewChangelog && (
        <div className="text-xs text-muted-foreground pt-1 border-t">
          Click to view changelog
        </div>
      )}
    </div>
  )

  const badgeContent = (
    <div className="flex items-center gap-1">
      <Info className="h-3 w-3" />
      <span>v{versionInfo.version}</span>
      {showChangelog && onViewChangelog && (
        <History className="h-3 w-3 ml-1" />
      )}
    </div>
  )

  if (showChangelog && onViewChangelog) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onViewChangelog}
              className="h-6 px-2 py-1 text-xs font-medium"
            >
              {badgeContent}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={variant} className={`
            cursor-help
            ${size === 'sm' ? 'text-xs px-2 py-1' : ''}
            ${size === 'lg' ? 'text-sm px-3 py-2' : ''}
          `}>
            {badgeContent}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default VersionIndicator